import React, { useState } from "react";
import styled from "styled-components";
import { useDropzone } from "react-dropzone";
import {postWithAuth, postFileWithAuth } from "../Network/HttpMethods";
import { fetchUploadedFiles } from "../Network/ServiceMethods";
import { GreenButton } from "../Shared/RedButton";

const UploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #ffffff;
  padding: 20px 40px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
`;

const DropzoneArea = styled.div`
  width: 100%;
  padding: 20px;
  border: 2px dashed #007bff;
  border-radius: 8px;
  background-color: #f8f9fa;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #e9ecef;
  }
`;

const UrlInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  margin: 15px 0 10px;
  box-sizing: border-box;

  &:focus {
    border-color: #28a745;
    outline: none;
  }
`;

const UploadStatus = styled.p`
  font-size: 1rem;
  color: ${(props) => (props.success ? "green" : "red")};
  margin-top: 10px;
`;

const SmartUploadInput = ({ uploadUrl, setUploadedFiles, onUploadComplete }) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploadStatus, setUploadStatus] = useState("");
    const [url, setUrl] = useState("");

    const onDrop = (acceptedFiles) => {
        setSelectedFiles(acceptedFiles);
        setUploadStatus(""); // Reset status
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

    const handleSubmit = async () => {
        if (url.trim()) {
            try {
                setUploadStatus("Fetching file from URL...");
                const res = await postWithAuth(`${uploadUrl}/external/tiktok`, JSON.stringify({ url }), {
                    headers: { "Content-Type": "application/json" },
                });

                if (res.ok) {
                    setUploadStatus("File fetched and saved!");
                    setUrl("");
                    if (onUploadComplete) {
                        onUploadComplete();
                    } else if (setUploadedFiles) {
                        await fetchUploadedFiles(setUploadedFiles);
                    }
                } else {
                    const err = await res.text();
                    setUploadStatus(`Fetch failed: ${err}`);
                }
            } catch (e) {
                console.error("Fetch error:", e);
                setUploadStatus("An error occurred while fetching the URL.");
            }
            return;
        }

        if (selectedFiles.length === 0) {
            setUploadStatus("Please select or drop files first.");
            return;
        }

        const formData = new FormData();
        selectedFiles.forEach((file) => formData.append("file", file));

        try {
            setUploadStatus("Uploading...");
            const response = await postFileWithAuth(uploadUrl, formData);

            if (response.ok) {
                setUploadStatus("Files uploaded successfully!");
                setSelectedFiles([]);
                if (onUploadComplete) {
                    onUploadComplete();
                } else if (setUploadedFiles) {
                    await fetchUploadedFiles(setUploadedFiles);
                }
            } else {
                const errorText = await response.text();
                setUploadStatus(`Upload failed: ${errorText}`);
            }
        } catch (error) {
            console.error("Upload error:", error);
            setUploadStatus("An error occurred during the upload.");
        }
    };

    const actionLabel = url.trim() ? "Fetch File" : "Upload File";

    return (
        <UploadContainer>
            <DropzoneArea {...getRootProps()}>
                <input {...getInputProps()} />
                {isDragActive ? (
                    <p>Drop the files here...</p>
                ) : (
                    <p>Drag & drop some files here, or click to select.</p>
                )}
            </DropzoneArea>
            <UrlInput
                type="url"
                placeholder="Or paste a URL to fetch..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
            />

            {selectedFiles.length > 0 && (
                <p>
                    Selected Files:{" "}
                    {selectedFiles.map((file) => file.name).join(", ")}
                </p>
            )}

            <GreenButton onClick={handleSubmit}>{actionLabel}</GreenButton>

            {uploadStatus && (
                <UploadStatus success={uploadStatus.includes("successfully") || uploadStatus.includes("saved")}>
                    {uploadStatus}
                </UploadStatus>
            )}
        </UploadContainer>
    );
};

export default SmartUploadInput;
